/**
 * DataTabs — lazy-mounted, URL-synced tab layout.
 *
 * Only the active tab is rendered → inactive tabs contribute zero render
 * cost (no hidden DOM, no useEffect chains running in background).
 *
 * Tab key is reflected to `?tab=<key>` so refresh / shared links work.
 *
 * Usage:
 *   <DataTabs
 *     tabs={[
 *       { key: 'snapshot', label: 'Snapshot', icon: BarChart3, render: () => <Snapshot /> },
 *       { key: 'daily',    label: 'Daily',    render: () => <Daily />, count: 1234 },
 *     ]}
 *     defaultTab="snapshot"
 *     urlParam="tab"
 *   />
 */

import React, { useState, useEffect, useCallback } from 'react';

const readUrlTab = (param) => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(param);
};

const writeUrlTab = (param, value) => {
  if (typeof window === 'undefined') return;
  const usp = new URLSearchParams(window.location.search);
  if (value) usp.set(param, value); else usp.delete(param);
  const next = `${window.location.pathname}?${usp.toString()}${window.location.hash}`;
  window.history.replaceState(null, '', next);
};

export const DataTabs = ({
  tabs,
  defaultTab,
  urlParam = 'tab',
  className = '',
  testid = 'data-tabs',
  onTabChange,
}) => {
  const initial = (() => {
    const fromUrl = readUrlTab(urlParam);
    if (fromUrl && tabs.some((t) => t.key === fromUrl)) return fromUrl;
    return defaultTab || (tabs[0] && tabs[0].key);
  })();
  const [active, setActive] = useState(initial);

  useEffect(() => {
    writeUrlTab(urlParam, active);
    onTabChange?.(active);
  }, [active, urlParam, onTabChange]);

  const onSelect = useCallback((key) => setActive(key), []);

  const activeTab = tabs.find((t) => t.key === active) || tabs[0];

  return (
    <div className={`data-tabs ${className}`} data-testid={testid}>
      <div
        className="flex items-center gap-1 border-b border-neutral-200 mb-5 overflow-x-auto"
        role="tablist"
      >
        {tabs.map((t) => {
          const isActive = t.key === active;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(t.key)}
              data-testid={`${testid}-tab-${t.key}`}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors
                ${isActive
                  ? 'text-[#1a1a2e] border-b-2 border-pink-500 -mb-px'
                  : 'text-neutral-500 hover:text-neutral-800 border-b-2 border-transparent'}`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              <span>{t.label}</span>
              {typeof t.count === 'number' && (
                <span className={`ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-semibold rounded-full
                  ${isActive ? 'bg-pink-100 text-pink-700' : 'bg-neutral-100 text-neutral-600'}`}>
                  {t.count > 999 ? `${Math.floor(t.count / 1000)}k+` : t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" key={activeTab?.key}>
        {/* `key` forces a fresh mount on every tab switch — no stale state */}
        {activeTab?.render?.()}
      </div>
    </div>
  );
};

export default DataTabs;
